import React, { useEffect, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import DashLoader from "../../components/DashLoader";
import {
  getTokenUsageSummary,
  getTokenUsageDaily,
  getTokenUsageByModel,
  getTokenUsageByUser
} from '../../apis/index';
import "../../styles/Dashboard.css";
import "../../styles/EngagementDashboard.css";

// Register Chart.js plugins
Chart.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement);

const TokenDashboard = ({ selectedDatasetId, globalStartDate, globalEndDate, globalSelectedUsers }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dashboard data state
  const [summaryData, setSummaryData] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [modelData, setModelData] = useState([]);
  const [userData, setUserData] = useState([]);

  // Format date to YYYY-MM-DD
  const formatDateStr = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  // Fetch all dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    const startDateStr = formatDateStr(globalStartDate);
    const endDateStr = formatDateStr(globalEndDate);
    
    // For multi-select users, pass the array directly
    const userIdsArray = globalSelectedUsers && globalSelectedUsers.length > 0 
      ? globalSelectedUsers 
      : null;

    try {
      const [summary, daily, model, usersResp] = await Promise.all([
        getTokenUsageSummary(startDateStr, endDateStr, undefined, selectedDatasetId, userIdsArray),
        getTokenUsageDaily(startDateStr, endDateStr, undefined, selectedDatasetId, userIdsArray),
        getTokenUsageByModel(startDateStr, endDateStr, undefined, selectedDatasetId, userIdsArray),
        getTokenUsageByUser(startDateStr, endDateStr, undefined, selectedDatasetId, userIdsArray, 10) // Filter by selected users
      ]);

      // API returns { success: true, data: {...} }, extract the inner data object
      setSummaryData(summary?.data?.data);
      setDailyData(daily?.data?.data || []);
      setModelData(model?.data?.data || []);
      setUserData(usersResp?.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch token metrics:', err);
      setError('Failed to load token usage data. Please check if the metrics API is configured and running.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch data when both dates are set (for range picker) and valid
    if (globalStartDate && globalEndDate && globalStartDate <= globalEndDate) {
      fetchDashboardData();
    }
  }, [globalStartDate?.getTime(), globalEndDate?.getTime(), selectedDatasetId, JSON.stringify(globalSelectedUsers)]);

  // Format numbers for display
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatCost = (cost) => '$' + (cost || 0).toFixed(4);

  // Chart configuration for daily token trend (Bar chart style matching Engagement Dashboard)
  const dailyChartData = {
    labels: dailyData.map(d => {
      const date = new Date(d.date);
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        data: dailyData.map(d => d.totalTokens),
        borderColor: '#0053ED',
        backgroundColor: '#0053ED',
        borderWidth: 1,
        barPercentage: 0.3,
      },
    ],
  };

  const dailyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#0053ED',
        borderWidth: 1,
        displayColors: false,
        callbacks: {
          label: (context) => `Tokens: ${context.parsed.y.toLocaleString()}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
        ticks: {
          stepSize: 1,
          callback: (value) => formatNumber(value),
        },
        title: {
          display: true,
          text: 'Tokens',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: 'Day',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
      },
    },
  };

  // Chart configuration for model breakdown
  const modelChartData = {
    labels: modelData.map(m => m.modelName),
    datasets: [
      {
        data: modelData.map(m => m.totalTokens),
        backgroundColor: [
          '#0053ED', // Primary blue
          '#10B981', // Green
          '#F59E0B', // Amber
          '#EF4444', // Red
          '#8B5CF6', // Purple
          '#EC4899', // Pink
          '#06B6D4', // Cyan
          '#F97316', // Orange
        ],
        borderWidth: 3,
        borderColor: '#fff',
        hoverOffset: 8,
      },
    ],
  };

  const modelChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20,
        bottom: 20,
        left: 20,
        right: 20,
      },
    },
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 15,
          color: '#666666',
          font: {
            size: 13,
          },
          usePointStyle: true,
          pointStyle: 'circle',
          maxWidth: 300,
          generateLabels: (chart) => {
            const data = chart.data;
            return data.labels.map((label, i) => ({
              text: `${label} (${formatNumber(data.datasets[0].data[i])})`,
              fillStyle: data.datasets[0].backgroundColor[i],
              strokeStyle: data.datasets[0].backgroundColor[i],
              fontColor: '#666666',
              hidden: false,
              index: i,
            }));
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#0053ED',
        borderWidth: 1,
        displayColors: true,
        callbacks: {
          label: (context) => {
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${value.toLocaleString()} tokens (${percentage}%)`;
          },
        },
      },
    },
  };

  // Render summary cards
  const renderSummaryCards = () => {
    const kpiGradient = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

    const cards = [
      {
        title: 'Total Tokens',
        value: formatNumber(summaryData?.totalTokens),
        subtitle: (
          <>
            <span className="kpi-param-label">Prompt:</span>{' '}
            <span className="kpi-param-value">{formatNumber(summaryData?.totalPromptTokens)}</span>
            <span className="kpi-param-separator"> | </span>
            <span className="kpi-param-label">Completion:</span>{' '}
            <span className="kpi-param-value">{formatNumber(summaryData?.totalCompletionTokens)}</span>
          </>
        ),
      },
      {
        title: 'Total Requests',
        value: formatNumber(summaryData?.totalRequests),
        subtitle: null,
      },
    ];

    return (
      <div className="dashboard-cards token-kpi-cards">
        {cards.map((card, index) => (
          <div 
            key={index} 
            className="card_dashboard simple-kpi-card"
          >
            <p className="kpi-title">{card.title}</p>
            <h3 className="kpi-value kpi-value-primary">
              {loading ? '...' : card.value}
            </h3>
            {card.subtitle && <p className="kpi-subtitle">{card.subtitle}</p>}
          </div>
        ))}
      </div>
    );
  };

  // Render top users table
  const renderTopUsers = () => {
    if (!userData || userData.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#6B7280' }}>
          <p>No user data available</p>
        </div>
      );
    }

    const tableStyle = {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '14px',
      border: 'none'
    };

    const thStyle = {
      padding: '12px',
      textAlign: 'left',
      fontWeight: '600',
      border: 'none'
    };

    const tdStyle = {
      padding: '12px',
      textAlign: 'left',
      border: 'none'
    };

    return (
      <div className="token-users-table-wrapper" style={{ overflowX: 'auto', width: '100%' }}>
        <table style={{ ...tableStyle, minWidth: '400px' }}>
          <thead>
            <tr>
              <th style={thStyle}>User</th>
              <th style={thStyle}>Tokens</th>
              <th style={thStyle}>Sessions</th>
              <th style={thStyle}>Questions</th>
            </tr>
          </thead>
          <tbody>
            {userData.map((user, index) => (
              <tr key={user.userId || index}>
                <td style={tdStyle}>{user.userName || user.userId}</td>
                <td style={{ ...tdStyle, color: '#666666', fontWeight: '600' }}>
                  {formatNumber(user.totalTokens)}
                </td>
                <td style={{ ...tdStyle, color: '#666666', fontWeight: '600' }}>{user.sessions || 0}</td>
                <td style={{ ...tdStyle, color: '#666666', fontWeight: '600' }}>{user.requests || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="dashboard">
      {/* Error Message */}
      {error && (
        <div style={{
          gridColumn: '1 / -1',
          background: '#FEE2E2',
          color: '#DC2626',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '15px',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Summary Cards */}
      {renderSummaryCards()}

      {/* Daily Token Usage Chart */}
      <div className="card_dashboard" style={{ position: 'relative', overflow: 'hidden' }}>
        <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '18px', color: '#1F2937' }}>
          Token Usage Trend
        </h3>
        <div className="chart-containers">
          {loading ? (
            <DashLoader />
          ) : (
            <Bar data={dailyChartData} options={dailyChartOptions} />
          )}
        </div>
      </div>

      {/* Model Breakdown Chart */}
      <div className="card_dashboard" style={{ position: 'relative', overflow: 'hidden' }}>
        <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '18px', color: '#1F2937' }}>
          Usage by Model
        </h3>
        <div className="chart-containers">
          {loading ? (
            <DashLoader />
          ) : (
            <Doughnut data={modelChartData} options={modelChartOptions} />
          )}
        </div>
      </div>

      {/* Top Users Table */}
      <div className="card_dashboard" style={{ position: 'relative' }}>
        <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '18px', color: '#1F2937' }}>
          Top Users by Token Usage
        </h3>
        {loading ? (
          <DashLoader />
        ) : renderTopUsers()}
      </div>
    </div>
  );
};

export default TokenDashboard;
